import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageSquare, Link as LinkIcon, BookOpen } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { ChatThread, Source, KnowledgeItem } from "@shared/schema";

interface LinkDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  documentId: string;
}

export function LinkDocumentDialog({ open, onClose, documentId }: LinkDocumentDialogProps) {
  const { toast } = useToast();
  const [selectedChatThreads, setSelectedChatThreads] = useState<Set<string>>(new Set());
  const [selectedSources, setSelectedSources] = useState<Set<string>>(new Set());
  const [selectedKnowledgeItems, setSelectedKnowledgeItems] = useState<Set<string>>(new Set());

  const { data: chatThreads = [] } = useQuery<ChatThread[]>({
    queryKey: ["/api/chat-threads"],
  });
    if (chatThreads) {
        console.log(`-------------------------chatThreads-`);
        console.log(`-------------------------chatThreads-`);
        console.log(`-------------------------chatThreads-`);
        console.log(`-------------------------chatThreads-`);
        console.log(chatThreads);
    }

  const { data: sources = [] } = useQuery<Source[]>({
    queryKey: ["/api/sources"],
  });

  const { data: knowledgeItems = [] } = useQuery<KnowledgeItem[]>({
    queryKey: ["/api/knowledge-items"],
  });

  const { data: existingLinks, refetch: refetchLinks } = useQuery({
    queryKey: [`/api/documents/${documentId}/links`],
    enabled: open,
    staleTime: 0,
  });

  // Force refetch when dialog opens
  useEffect(() => {
    if (open) {
      refetchLinks();
    }
  }, [open, refetchLinks]);

  // Reset and preload selections when dialog opens or document changes
  useEffect(() => {
    if (open && existingLinks) {
      const links = existingLinks as any;
      setSelectedChatThreads(new Set(links?.chatThreads?.map((l: any) => l.chatThreadId) || []));
      setSelectedSources(new Set(links?.sources?.map((l: any) => l.sourceId) || []));
      setSelectedKnowledgeItems(new Set(links?.knowledgeItems?.map((l: any) => l.knowledgeItemId) || []));
    } else if (!open) {
      // Clear selections when dialog closes
      setSelectedChatThreads(new Set());
      setSelectedSources(new Set());
      setSelectedKnowledgeItems(new Set());
    }
  }, [open, documentId, existingLinks]);

  const linkChatThreadMutation = useMutation({
    mutationFn: async (chatThreadId: string) => {
      return await apiRequest("POST", `/api/documents/${documentId}/links/chat-threads`, {
        chatThreadId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/documents/${documentId}/links`] });
    },
  });

  const unlinkChatThreadMutation = useMutation({
    mutationFn: async (chatThreadId: string) => {
      return await apiRequest("DELETE", `/api/documents/${documentId}/links/chat-threads/${chatThreadId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/documents/${documentId}/links`] });
    },
  });

  const linkSourceMutation = useMutation({
    mutationFn: async (sourceId: string) => {
      return await apiRequest("POST", `/api/documents/${documentId}/links/sources`, {
        sourceId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/documents/${documentId}/links`] });
    },
  });

  const unlinkSourceMutation = useMutation({
    mutationFn: async (sourceId: string) => {
      return await apiRequest("DELETE", `/api/documents/${documentId}/links/sources/${sourceId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/documents/${documentId}/links`] });
    },
  });

  const linkKnowledgeItemMutation = useMutation({
    mutationFn: async (knowledgeItemId: string) => {
      return await apiRequest("POST", `/api/documents/${documentId}/links/knowledge-items`, {
        knowledgeItemId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/documents/${documentId}/links`] });
    },
  });

  const unlinkKnowledgeItemMutation = useMutation({
    mutationFn: async (knowledgeItemId: string) => {
      return await apiRequest("DELETE", `/api/documents/${documentId}/links/knowledge-items/${knowledgeItemId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/documents/${documentId}/links`] });
    },
  });

  const handleSave = async () => {
    const links = (existingLinks as any) || { chatThreads: [], sources: [], knowledgeItems: [] };
    const existingChatThreadIds = new Set(links.chatThreads?.map((l: any) => l.chatThreadId) || []);
    const existingSourceIds = new Set(links.sources?.map((l: any) => l.sourceId) || []);
    const existingKnowledgeItemIds = new Set(links.knowledgeItems?.map((l: any) => l.knowledgeItemId) || []);

    console.log('[DEBUG] existingLinks:', existingLinks);
    console.log('[DEBUG] existingSourceIds:', Array.from(existingSourceIds));
    console.log('[DEBUG] selectedSources:', Array.from(selectedSources));

    const promises = [];

    // Add new links
    for (const chatThreadId of Array.from(selectedChatThreads)) {
      if (!existingChatThreadIds.has(chatThreadId)) {
        console.log('[DEBUG] POST chat thread:', chatThreadId);
        promises.push(linkChatThreadMutation.mutateAsync(chatThreadId));
      }
    }

    for (const sourceId of Array.from(selectedSources)) {
      if (!existingSourceIds.has(sourceId)) {
        console.log('[DEBUG] POST source:', sourceId);
        promises.push(linkSourceMutation.mutateAsync(sourceId));
      }
    }

    for (const knowledgeItemId of Array.from(selectedKnowledgeItems)) {
      if (!existingKnowledgeItemIds.has(knowledgeItemId)) {
        console.log('[DEBUG] POST knowledge item:', knowledgeItemId);
        promises.push(linkKnowledgeItemMutation.mutateAsync(knowledgeItemId));
      }
    }

    // Remove unchecked links
    for (const chatThreadId of Array.from(existingChatThreadIds)) {
      if (!selectedChatThreads.has(chatThreadId)) {
        console.log('[DEBUG] DELETE chat thread:', chatThreadId);
        promises.push(unlinkChatThreadMutation.mutateAsync(chatThreadId));
      }
    }

    for (const sourceId of Array.from(existingSourceIds)) {
      if (!selectedSources.has(sourceId)) {
        console.log('[DEBUG] DELETE source:', sourceId);
        promises.push(unlinkSourceMutation.mutateAsync(sourceId));
      }
    }

    for (const knowledgeItemId of Array.from(existingKnowledgeItemIds)) {
      if (!selectedKnowledgeItems.has(knowledgeItemId)) {
        console.log('[DEBUG] DELETE knowledge item:', knowledgeItemId);
        promises.push(unlinkKnowledgeItemMutation.mutateAsync(knowledgeItemId));
      }
    }

    await Promise.all(promises);
    toast({ title: "Links updated successfully" });
    onClose();
  };

  const toggleChatThread = (id: string) => {
    setSelectedChatThreads(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSource = (id: string) => {
    setSelectedSources(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleKnowledgeItem = (id: string) => {
    setSelectedKnowledgeItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Link Document</DialogTitle>
          <DialogDescription>
            Select entities to link with this document. You can link to chat threads, sources, and knowledge items.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="chat-threads" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="chat-threads" data-testid="tab-chat-threads">
              <MessageSquare className="w-4 h-4 mr-2" />
              Chat Threads
            </TabsTrigger>
            <TabsTrigger value="sources" data-testid="tab-sources">
              <LinkIcon className="w-4 h-4 mr-2" />
              Sources
            </TabsTrigger>
            <TabsTrigger value="knowledge" data-testid="tab-knowledge">
              <BookOpen className="w-4 h-4 mr-2" />
              Knowledge Items
            </TabsTrigger>
          </TabsList>

          <TabsContent value="chat-threads" className="mt-4">
            <ScrollArea className="h-[300px] pr-4">
              {chatThreads.length === 0 ? (
                <p className="text-sm text-muted-foreground">No chat threads available</p>
              ) : (
                <div className="space-y-2">
                  {chatThreads.map((thread) => (
                    <div key={thread.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`chat-${thread.id}`}
                        checked={selectedChatThreads.has(thread.id)}
                        onCheckedChange={() => toggleChatThread(thread.id)}
                        data-testid={`checkbox-chat-${thread.id}`}
                      />
                      <label
                        htmlFor={`chat-${thread.id}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                      >
                        {thread.title || "Untitled Thread"}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          <TabsContent value="sources" className="mt-4">
            <ScrollArea className="h-[300px] pr-4">
              {sources.length === 0 ? (
                <p className="text-sm text-muted-foreground">No sources available</p>
              ) : (
                <div className="space-y-2">
                  {sources.map((source) => (
                    <div key={source.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`source-${source.id}`}
                        checked={selectedSources.has(source.id)}
                        onCheckedChange={() => toggleSource(source.id)}
                        data-testid={`checkbox-source-${source.id}`}
                      />
                      <label
                        htmlFor={`source-${source.id}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                      >
                        {source.title}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          <TabsContent value="knowledge" className="mt-4">
            <ScrollArea className="h-[300px] pr-4">
              {knowledgeItems.length === 0 ? (
                <p className="text-sm text-muted-foreground">No knowledge items available</p>
              ) : (
                <div className="space-y-2">
                  {knowledgeItems.map((item) => (
                    <div key={item.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`knowledge-${item.id}`}
                        checked={selectedKnowledgeItems.has(item.id)}
                        onCheckedChange={() => toggleKnowledgeItem(item.id)}
                        data-testid={`checkbox-knowledge-${item.id}`}
                      />
                      <label
                        htmlFor={`knowledge-${item.id}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                      >
                        {item.title}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose} data-testid="button-cancel-link">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={
              selectedChatThreads.size === 0 &&
              selectedSources.size === 0 &&
              selectedKnowledgeItems.size === 0
            }
            data-testid="button-save-links"
          >
            Link Selected
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
